<script setup lang="ts">
import { TriangleAlert } from 'lucide-vue-next'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { AudienceRule, Rules } from '@/types/types'
import { useAudienceRulesFormatter } from '../composables/useAudienceRulesFormatter'

interface Props {
  isOpen: boolean
  icon: Icons
  audienceName: string
  audienceRules?: Partial<Record<Rules, AudienceRule>>
  templateTitle: string
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  icon: 'HeroUserIconSolid',
  audienceName: '',
  audienceRules: undefined,
  templateTitle: ''
})
const emit = defineEmits<{
  close: []
  confirm: []
  edit: []
}>()

const { t } = useI18n()
const { formatRules } = useAudienceRulesFormatter()

const formattedRules = computed(() => {
  if (!props.audienceRules) return []
  return formatRules(props.audienceRules)
})

const handleConfirm = () => {
  emit('confirm')
}

const handleClose = () => {
  emit('close')
}

const handleEdit = () => {
  emit('edit')
}
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    size="lg"
    :link-text="t('pages.autopilot.autopilotsPage.templates.confirmation.createButton')"
    :cancel="t('global.words.cancel')"
    @close="handleClose"
    @click="handleConfirm"
  >
    <div class="flex flex-col gap-6">
      <div class="flex justify-center">
        <div class="bg-yellow-50 p-2.5 rounded-full">
          <TriangleAlert class="h-6 w-6 text-yellow-500" />
        </div>
      </div>

      <div class="flex flex-col gap-1.5 text-center">
        <h3 class="text-base font-semibold text-foreground">
          {{ t('pages.autopilot.autopilotsPage.templates.confirmation.title') }}
        </h3>
        <p class="text-sm font-normal text-foreground-secondary">"{{ audienceName }}"</p>
      </div>

      <BaseTip
        :text="
          t('pages.autopilot.autopilotsPage.templates.confirmation.infoMessage', {
            audienceName: audienceName
          })
        "
        severity="info"
      />

      <div class="bg-muted border border-border rounded-xl p-4 flex flex-col gap-4">
        <div class="flex items-center gap-2.5">
          <div class="bg-white border border-border p-2 rounded-xl shrink-0">
            <BaseIcon :name="icon" class="h-5 w-5 text-foreground-secondary" />
          </div>
          <p class="flex-1 text-base font-medium text-foreground">
            {{ audienceName }}
          </p>
          <Button variant="outline" class="bg-white text-foreground border-border hover:bg-accent" @click="handleEdit">
            <span class="text-sm font-medium text-foreground"
              >{{ t('pages.autopilot.autopilotsPage.templates.confirmation.modifyButton') }}
            </span>
            <BaseIcon name="IconoirEditPencil" class="h-5 w-5 text-foreground-secondary" />
          </Button>
        </div>

        <div class="flex flex-col gap-2">
          <p class="text-sm text-foreground-secondary">
            {{ t('pages.autopilot.autopilotsPage.templates.confirmation.targetingCriteria') }}
          </p>
          <ul v-if="formattedRules.length > 0" class="list-disc list-inside space-y-1">
            <li v-for="(rule, index) in formattedRules" :key="index" class="text-sm text-foreground-secondary">
              {{ rule }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
